//src/hooks/useGroups.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createGroup,
  deleteGroup,
  getGroupDetail,
  listGroups,
  listPermissions,
  patchGroupStatus,
  updateGroup,
  updateGroupPermissions,
  type GroupPermissionsUpdateIn,
  type GroupScope,
  type GroupStatusPatchIn,
  type GroupUpsertIn,
} from "@/services/groups";

export function useGroups(scope?: GroupScope, includeInactive = false, enabled = true) {
  return useQuery({
    queryKey: ["groups", scope ?? "all", { includeInactive }],
    queryFn: () => listGroups(includeInactive, scope),
    enabled,
  });
}

export function useGroupDetail(groupId?: string) {
  return useQuery({
    queryKey: ["group_detail", groupId],
    queryFn: () => getGroupDetail(groupId as string),
    enabled: !!groupId,
  });
}

export function usePermissionsCatalog(scope?: GroupScope, enabled = true) {
  return useQuery({
    queryKey: ["permissions_catalog", scope],
    queryFn: () => listPermissions(scope as GroupScope),
    staleTime: 60_000,
    enabled: enabled && !!scope,
  });
}

function invalidateGroups(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ["groups"] });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GroupUpsertIn) => createGroup(payload),
    onSuccess: () => void invalidateGroups(qc),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: GroupUpsertIn }) => updateGroup(id, body),
    onSuccess: (_data, vars) => {
      void invalidateGroups(qc);
      void qc.invalidateQueries({ queryKey: ["group_detail", vars.id] });
    },
  });
}

export function usePatchGroupStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: GroupStatusPatchIn }) => patchGroupStatus(id, body),
    onSuccess: (_data, vars) => {
      void invalidateGroups(qc);
      void qc.invalidateQueries({ queryKey: ["group_detail", vars.id] });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => deleteGroup(groupId),
    onSuccess: () => void invalidateGroups(qc),
  });
}

export function useUpdateGroupPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: GroupPermissionsUpdateIn }) =>
      updateGroupPermissions(id, body),
    onSuccess: (_data, vars) => {
      void invalidateGroups(qc);
      void qc.invalidateQueries({ queryKey: ["group_detail", vars.id] });
    },
  });
}
